import { Progress as AntProgress, Spin, Col, Form, message, Modal } from "antd";
import Button from "atoms/Button";
import Table from "atoms/Table";
import Title from "atoms/Title";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import COLORS from "common/Colors";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useQueryState } from "hooks/useQueryState";
import { useScrollFetch } from "hooks/useScrollFetch";
import { useScrollFetch2 } from "hooks/useScrollFetch2";
import { useClientsListSource, getClientsListPageData } from "hooks/useClientsListSource";
import { find, sumBy, uniqBy, some, includes } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Row } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { isEqual } from "lodash";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { getUserList, getUserInfo, getLoadingRoute, getHigherOfficials } from "redux/selectors";
import { STATUS_CODE } from "common/Constants";
import styled from "styled-components";
import {
  setTempEngagementFormData,
  setselectedengagementtab,
} from "redux/actions";
import Icon from "atoms/Icon";
import { useVT } from "virtualizedtableforantd4";
import { CALL_API } from "common/API";
import { getEmailsByRole } from "utils/functions";

const EngagementWrapper = styled.div`
  .ant-table-body {
    min-height: 300px;
    max-height: 600px !important;
  }
  @media (max-width: 1324px) {
    .ant-table-body {
      min-height: 200px;
      max-height: 300px !important;
    }
  }
  .title {
    display: flex;
    justify-content: space-between;
  }

  .search-box {
    max-width: 250px;
    margin-bottom: 8px;
  }

  .green-dot {
    height: 15px;
    content: "";
    width: 22px;
    background-color: #4caf50;
    border-radius: 9999px;
    display: inline-block;
  }
  .table-row {
    cursor: pointer;
  }

  .custom-filter-btn {
    margin-bottom: 0px !important;
  }

  .pagination-loader {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const Progress = styled(AntProgress)`
  .ant-progress-inner {
    border-radius: 5px !important;
  }
  .ant-progress-bg {
    height: 16px !important;
    border-radius: 5px !important;
    position: relative;
    transition: none !important; /* Disable the animation */
    &::before {
      content: ${({ percentContent }) => percentContent};
      /* content: "66.66%"; */
      position: absolute;
      top: -4px;
      padding-left: 5px;
    }
  }
`;

const StyledLoader = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  ${({ isHidden }) => isHidden && "visibility: hidden;"}
`;

const StyledTable = styled(Table)`
  width: 100%;
  table-layout: fixed;
`;

const Engagement = () => {
  const [form] = Form.useForm();
  const formsManage = Form.useFormInstance();
  const userList = useSelector((state) => getUserList(state));
  const userInfo = useSelector((state) => getUserInfo(state));
  const [deletingId, setDeletingId] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false); // New state for delete loader
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const loadingRoute = useSelector((state) => getLoadingRoute(state));
  const higherOfficialsList = useSelector((state) => getHigherOfficials(state));
  const importantPeople = getEmailsByRole(higherOfficialsList, "engagementHigherUsers");
  const partnerGrade = ["P1", "P2", "P3", "P4", "P5"];
  const [vt, set_components] = useVT(() => ({ scroll: { y: 600 } }), []);
  const activeUserslist = userList.filter((item) => item.statusid === "1");
  const partnerOptions = activeUserslist
    ?.filter((user) => {
      return partnerGrade.some((grade) => grade === user.grade);
    })
    .map((partner) => ({
      label: partner.fullname,
      value: partner.userid,
    }));
  const isPartner =
    partnerOptions.some((partner) => partner.value === userInfo?.userid) ||
    userInfo.managingoffice[0] === "Head Office";

  const showDeleteModal = (record) => {
    setRecordToDelete(record);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    const { clientId, engagementId, country } = recordToDelete;
    setDeletingId(engagementId);
    try {
      const { code, Message } = await CALL_API(
        `/insyts/engagement?clientId=${clientId}&engagementId=${engagementId}&country=${country}`,
        "delete"
      );

      if (code === STATUS_CODE.SUCCESS) {
        message.success(Message || "Deleted successfully");
        setLocalData((prev) =>
          prev.filter(
            (item) =>
              !(
                item.clientId === clientId &&
                item.engagementId === engagementId &&
                item.country.toLowerCase() === country.toLowerCase()
              )
          )
        );

        await refetch();
        setDeleteModalVisible(false); // Close modal after successful deletion
      } else {
        message.error(Message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete failed:", err);
      message.error("Something went wrong. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setRecordToDelete(null);
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "employee",
      className: "normal-column ",
      width: 180,
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (val) => {
        return <span className="text-primary">{val}</span>;
      },
      onCell: (record) => ({
        onClick: () =>
          history.push("/engagement/view", { id: record.engagementId }),
        style: { cursor: "pointer" },
      }),
    },
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      className: "normal-column",
      width: 150,
      sorter: (a, b) => a.clientName.localeCompare(b.clientName),
    },
    // {
    //   title: "Overall",
    //   dataIndex: "overall",
    //   key: "overall",
    //   className: "normal-column",
    //   width: 85,
    //   align: "center",
    // },
    {
      title: "%CPL",
      dataIndex: "cpl",
      key: "cpl",
      className: "normal-column",
      width: 150,
      ellipsis: {
        showTitle: true,
      },
      align: "center",
      sorter: (a, b) =>
        parseFloat(a.cpl.props.percent) - parseFloat(b.cpl.props.percent),
    },
    {
      title: "Partner",
      dataIndex: "partner",
      key: "partner",
      className: "normal-column",
      sorter: (a, b) => a.partner.localeCompare(b.partner),
      render: (val) => {
        return <span>{find(userList, { userid: val })?.fullname || "-"}</span>;
      },
      width: 150,
    },
    {
      title: "Person In Charge",
      dataIndex: "manager",
      key: "manager",
      className: "normal-column",
      sorter: (a, b) => a.manager.localeCompare(b.manager),
      render: (val) => {
        return <span>{find(userList, { userid: val })?.fullname || "-"}</span>;
      },
      width: 150,
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      className: "normal-column",
      width: 150,
      sorter: (a, b) => a.country.localeCompare(b.country),
    },
    {
      title: "Competency",
      dataIndex: "competency",
      key: "competency",
      className: "normal-column",
      width: 150,
      sorter: (a, b) => a.competency.localeCompare(b.competency),
      render: (val) => {
        return <span>{val || "-"}</span>;
      },
    },
    {
      title: "Service Line",
      dataIndex: "serviceLine",
      key: "serviceLine",
      className: "normal-column",
      width: 150,
      sorter: (a, b) => a.serviceLine.localeCompare(b.serviceLine),
      render: (val) => {
        return <span>{val || "-"}</span>;
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      className: "normal-column",
      width: 120,
      sorter: (a, b) => a.type.localeCompare(b.type),
      render: (val) => {
        return <span className="text-primary">{val}</span>;
      },
      onCell: (record) => ({
        onClick: () =>
          history.push("/engagement/financialsummary", {
            id: record.engagementId,
          }),
        style: { cursor: "pointer" },
      }),
    },
    // {
    //   title: "Created By",
    //   dataIndex: "createdBy",
    //   key: "createdBy",
    //   className: "normal-column",
    //   width: 150,
    // },
    {
      title: "Plan Start Date",
      dataIndex: "planStartDate",
      key: "planStartDate",
      className: "normal-column",
      width: 150,
      sorter: (a, b) => new Date(a.planStartDate) - new Date(b.planStartDate),
    },
    {
      title: "Plan End Date",
      dataIndex: "planEndDate",
      key: "planEndDate",
      className: "normal-column",
      width: 150,
      sorter: (a, b) => new Date(a.planEndDate) - new Date(b.planEndDate),
    },
    // {
    //   title: "Date Updated",
    //   dataIndex: "dateupdated",
    //   key: "dateupdated",
    //   className: "normal-column",
    //   width: 140,
    // },
    // {
    //   title: "Upcoming Activities",
    //   dataIndex: "upcomingactivities",
    //   key: "upcomingactivities",
    //   className: "normal-column",
    //   width: 160,
    // },
    // {
    //   title: "Date Approved",
    //   dataIndex: "dateapproved",
    //   key: "dateapproved",
    //   className: "normal-column",
    //   width: 150,
    // },
    {
      title: "Engagement Status",
      dataIndex: "engagementStatus",
      key: "engagementStatus",
      className: "normal-column",
      width: 160,
      sorter: (a, b) => a.engagementStatus.localeCompare(b.engagementStatus),
    },
    {
      title: "PFA Status",
      dataIndex: "pfaStatus",
      key: "pfaStatus",
      className: "normal-column",
      width: 150,
      sorter: (a, b) => a.pfaStatus.localeCompare(b.pfaStatus),
    },
    {
      title: "",
      key: "delete",
      width: 50,
      render: (text, record) =>
        record.engagementStatus === "Not Started" ? (
          <div style={{ textAlign: "center" }}>
            <Icon
              name="trash"
              style={{ color: "red", cursor: "pointer" }}
              onClick={() => showDeleteModal(record)}
            />
          </div>
        ) : null,
    },
  ];

  const engagementStatusItems = [
    {
      id: 1,
      value: "Not Approved",
    },
    {
      id: 2,
      value: "Not Started",
    },
    {
      id: 3,
      value: "Active",
    },
    {
      id: 4,
      value: "Closed",
    },
  ];

  const pfaStatusItems = [
    {
      id: 1,
      value: "Not Applicable",
    },
    {
      id: 2,
      value: "Approved",
    },
    {
      id: 3,
      value: "Declined",
    },
  ];

  const history = useHistory();

  const [search, setSearch] = useState("");
  const dispatch = useDispatch();

  // Period, Staff Level, Organisation Level, Competency, Status

  const filteruser = userList?.filter(
    (item) => item?.userid === userInfo.userid
  );

  const { getEngagements } = queryKeys;

  const queryParams = new URLSearchParams();
  queryParams.append("managingoffice", filteruser[0]?.managingoffice);
  queryParams.append("userId", filteruser[0]?.userid);
  queryParams.append(
    "countryManagingPartner",
    filteruser[0]?.countryManagingPartner === "yes" ? "yes" : "no"
  );

  const queryString = queryParams.toString();
  const { key, url } = getEngagements(queryString);

  const { data, isLoading, isReloading, fetchNextPage, hasNextPage } =
    useScrollFetch2(key, {
      endPoint: url,
      queryParam: "LastEvaluatedKey",
    });

  const { key: clientkey, url: clienturl } = useClientsListSource();

  const { data: clientsDataRes, isLoading: loading } = useScrollFetch(
    clientkey,
    {
      endPoint: clienturl,
      queryParam: "LastEvaluatedKey",
    }
  );

  const engagements = useMemo(() => {
    return data?.pages?.reduce((acc, page) => {
      if (!page?.data) return acc;
      return [...acc, ...page.data]; // flatten all pages
    }, []) ?? [];
  }, [data?.pages]);

  const { key: engagementDropdownKey, url: engagementDropdownUrl } =
    queryKeys.getEngagementFilterDropdown(
      `?managingoffice=${filteruser[0]?.managingoffice}&userId=${filteruser[0]?.userid}`
    );

  const { data: engagementDropdownDataRes } = useFetch(
    engagementDropdownKey,
    engagementDropdownUrl
  );
  const [filteredEngagementList, setFilteredEngagementList] = useState([]);

  const { getAllClientFiltered, getEngagementCount } = queryKeys;

  const { isFetched: isClientsFetched } = useQueryState(
    getAllClientFiltered?.key
  );

  const { data: clientDataRes } = useFetch(
    getAllClientFiltered?.key,
    getAllClientFiltered?.url,
    {
      enabled: !isClientsFetched,
    }
  );

  const clientsData = useMemo(() => {
    return clientsDataRes?.pages?.reduce((acc, page) => {
      const pageData = getClientsListPageData(page);
      if (pageData === undefined) return acc;
      return [...acc, ...pageData];
    }, []);
  }, [clientsDataRes]);

  const clients = clientDataRes?.data;

  const { isFetched: isCountFetched } = useQueryState(getEngagementCount?.key);

  const { data: engagementCountRes } = useFetch(
    getEngagementCount?.key,
    getEngagementCount?.url,
    {
      enabled: !isCountFetched,
    }
  );

  const count = engagementCountRes?.response;

  function capitalizeWords(string) {
    if (!string) return "-";
    return string
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join("-");
  }

  const engagementList = useMemo(() => {
    if (!engagements || !clients) {
      return [];
    }
    return engagements?.map((item) => {
      const percentage =
        (parseInt(item?.actualMandays) /
          (sumBy(item?.employeeList, "personDay") * 8)) *
        100;
      const formattedPercentage = percentage ? percentage.toFixed(2) : "0.00";

      const actualPercentage =
        formattedPercentage !== "Infinity" ? formattedPercentage : "0.00";

      const client = clients.find((client) => client.bdo_id === item.clientId);

      const createdByName = userList.find(
        (user) => user.userid === item.createdBy
      )?.fullname;

      return {
        engagementId: item.engagementId,
        clientId: item.clientId,
        title: item.title || "-",
        clientName: item?.clientName || "-",
        overall: <div className="green-dot"></div>,
        cpl: (
          <Progress
            percent={actualPercentage}
            percentContent={`"${actualPercentage}"`}
            size={[300, 20]}
            strokeColor={COLORS.PRIMARY}
            showInfo={false}
          />
        ),
        manager: item.manager || "-",
        partner: item.partner || "-",
        country: item.country,
        type: item.type,
        planStartDate: item.planStartDate,
        planEndDate: item.planEndDate,
        dateupdated: item.updatedDate || "-",
        upcomingactivities: item.upcomingactivities || "-",
        dateapproved: item.approvedDate,
        engagementStatus: item.engagementStatus,
        pfaStatus: item.pfaStatus,
        createBy: createdByName || "-",
        competency: item.competency || "-",
        serviceLine: item.serviceLine || "-",
      };
    });
  }, [engagements, clients]);

  useEffect(() => {
    dispatch(setTempEngagementFormData());
    dispatch(setselectedengagementtab());
  }, [engagementList]);

  useEffect(() => {
    if (loadingRoute) {
      if (search) {
        const filteredList = engagementList.filter((item) => {
          return item.title.toLowerCase().includes(search.toLowerCase());
        });
        setFilteredEngagementList(filteredList);
      } else {
        setFilteredEngagementList(engagementList);
      }
    }
  }, [loadingRoute, search, engagementList]);

  const selectedCountry = form?.getFieldValue("country") || [];
  const selectedStatus = form?.getFieldValue("engagementStatus") || [];
  const countryCount =
    selectedCountry?.length > 0
      ? count
        ?.filter((item) => selectedCountry.includes(item?.countryname))
        ?.reduce((sum, item) => {
          let statusSum = 0;
          if (selectedStatus?.length > 0) {
            if (selectedStatus.includes("Active")) {
              statusSum += item?.active || 0;
            }
            if (selectedStatus.includes("Not Started")) {
              statusSum += item?.notstarted || 0;
            }
            if (selectedStatus.includes("Closed")) {
              statusSum += item?.closed || 0;
            }
            if (selectedStatus.includes("Not Approved")) {
              statusSum += item?.notapproved || 0;
            }
          } else {
            statusSum +=
              (item?.active || 0) +
              (item?.notstarted || 0) +
              (item?.closed || 0);
          }
          return sum + statusSum;
        }, 0)
      : selectedStatus?.length > 0
        ? count?.reduce((sum, item) => {
          let statusSum = 0;
          if (selectedStatus.includes("Active")) {
            statusSum += item?.active || 0;
          }
          if (selectedStatus.includes("Not Started")) {
            statusSum += item?.notstarted || 0;
          }
          if (selectedStatus.includes("Closed")) {
            statusSum += item?.closed || 0;
          }
          if (selectedStatus.includes("Not Approved")) {
            statusSum += item?.notapproved || 0;
          }
          return sum + statusSum;
        }, 0)
        : count?.reduce(
          (sum, item) =>
            sum +
            (item?.active || 0) +
            (item?.notstarted || 0) +
            (item?.closed || 0) +
            (item?.notapproved || 0),
          0
        );

  const name = form.getFieldValue("title") || [];
  const manager = form.getFieldValue("manager") || [];
  const partner = form.getFieldValue("partner") || [];
  const clientname = form.getFieldValue("clientname") || [];
  const country = form.getFieldValue("country") || [];
  const engagementStatus = form.getFieldValue("engagementStatus") || [];
  const pfaStatus = form.getFieldValue("pfaStatus") || [];
  const competency = form.getFieldValue("competency") || [];
  const serviceLine = form.getFieldValue("serviceLine") || [];
  const manageoffice = form.getFieldValue("managing_office") || [];

  const engagementQueryParams = new URLSearchParams();

  const appendQueryParams = (key, values) => {
    if (Array.isArray(values) && values?.length > 0) {
      values.forEach((value) => engagementQueryParams.append(key, value));
    }
  };

  appendQueryParams("jobTitle", name);
  appendQueryParams("personIncharge", manager);
  appendQueryParams("partner", partner);
  appendQueryParams("clientName", clientname);
  appendQueryParams("country", country);
  appendQueryParams("engagementStatus", engagementStatus);
  appendQueryParams("pfaStatus", pfaStatus);
  appendQueryParams("competency", competency);
  appendQueryParams("serviceline", serviceLine);

  if (filteruser[0]?.managingoffice[0] !== "Head Office") {
    appendQueryParams("country", filteruser[0]?.managingoffice);
  }

  const engagementQueryString = engagementQueryParams.toString();

  const { key: engagementFilterKey, url: engagementFilterUrl } =
    queryKeys.getEngagementFilter(`?${engagementQueryString}`);

  const {
    data: fetchedDatas,
    isLoading: isEngagementFilterLoading,
    isReloading: isfilterreloading,
    fetchNextPage: fetchnext,
    hasNextPage: hasfilternextpage,
    refetch,
  } = useScrollFetch2(engagementFilterKey, {
    endPoint: engagementFilterUrl,
    queryParam: "LastEvaluatedKey",
  });
  const fetchedData = useMemo(() => {
    return fetchedDatas?.pages?.reduce((acc, page) => {
      if (page?.data === undefined) return acc;
      return [...acc, ...page?.data];
    }, []);
  }, [fetchedDatas]);

  useEffect(() => {
    if (engagementQueryString) refetch();
  }, [engagementQueryString, refetch]);

  const dropdownFilteredList = useMemo(() => {
    if (!fetchedData) {
      return [];
    }
    return fetchedData?.map((item) => {
      const percentage =
        (parseInt(item?.actualMandays) /
          (sumBy(item?.employeeList, "personDay") * 8)) *
        100;
      const formattedPercentage = percentage ? percentage.toFixed(2) : "0.00";

      const actualPercentage =
        formattedPercentage !== "Infinity" ? formattedPercentage : "0.00";

      const createdByName = userList.find(
        (user) => user.userid === item.createdBy
      )?.fullname;

      const clientOptions = clientsData
        ?.filter((client) => {
          return (
            client?.bdo_id !== "NULL" &&
            item.country?.includes(client?.bdo_managingoffice)
          );
        })
        ?.map((client) => ({
          label: client?.registeredName,
          value: client?.clientId,
        }));

      const clientName = clientOptions?.find((client) => {
        return client?.value === item?.clientId;
      })?.label;

      return {
        engagementId: item.engagementId,
        title: item.title || "-",
        clientName: item?.clientName || clientName || "-",
        overall: <div className="green-dot"></div>,
        clientId: item.clientId,
        cpl: (
          <Progress
            percent={actualPercentage}
            percentContent={`"${actualPercentage}"`}
            size={[300, 20]}
            strokeColor={COLORS.PRIMARY}
            showInfo={false}
          />
        ),
        manager: item.manager || "-",
        partner: item.partner || "-",
        country: item.country,
        type: item.type,
        planStartDate: item.planStartDate,
        planEndDate: item.planEndDate,
        dateupdated: item.updatedDate || "-",
        upcomingactivities: item.upcomingactivities || "-",
        dateapproved: item.approvedDate,
        engagementStatus: item.engagementStatus,
        pfaStatus: item.pfaStatus,
        createBy: createdByName || "-",
        competency: item.competency || "-",
        serviceLine: item.serviceLine || "-",
      };
    });
  }, [fetchedData]);

  // const getFilteredData = () => {
  // 	if (
  // 		name.length > 0 ||
  // 		manager.length > 0 ||
  // 		partner.length > 0 ||
  // 		clientname.length > 0 ||
  // 		country.length > 0 ||
  // 		engagementStatus.length > 0 ||
  // 		pfaStatus.length > 0 ||
  // 		competency.length > 0 ||
  // 		serviceLine.length > 0
  // 	) {
  // 		return dropdownFilteredList?.filter((item) => {
  // 			const nameMatch =
  // 				name && name.length > 0 ? name.some((n) => item.title === n) : true;

  // 			const managerMatch =
  // 				manager && manager.length > 0 ? manager.includes(item.manager) : true;

  // 			const partnerMatch =
  // 				partner && partner.length > 0 ? partner.includes(item.partner) : true;

  // 			const clientNameMatch =
  // 				clientname && clientname.length > 0
  // 					? clientname.includes(item?.clientname || item?.clientName)
  // 					: true;

  // 			const countryMatch =
  // 				country && country.length > 0
  // 					? country.includes(item?.country)
  // 					: true;

  // 			const competencyMatch =
  // 				competency && competency.length > 0
  // 					? competency.includes(item?.competency)
  // 					: true;

  // 			const serviceLineMatch =
  // 				serviceLine && serviceLine.length > 0
  // 					? serviceLine.includes(item?.serviceLine)
  // 					: true;

  // 			const convertToLowerCase = (item) => item.toLowerCase();

  // 			const statusMatch =
  // 				engagementStatus && engagementStatus.length > 0
  // 					? engagementStatus
  // 						.map(convertToLowerCase)
  // 						.includes(item?.engagementStatus?.toLowerCase())
  // 					: true;

  // 			const pfaStatusMatch =
  // 				pfaStatus && pfaStatus.length > 0
  // 					? pfaStatus.includes(item?.pfaStatus)
  // 					: true;

  // 			return (
  // 				nameMatch &&
  // 				managerMatch &&
  // 				partnerMatch &&
  // 				clientNameMatch &&
  // 				countryMatch &&
  // 				competencyMatch &&
  // 				serviceLineMatch &&
  // 				statusMatch &&
  // 				pfaStatusMatch
  // 			);
  // 		});
  // 	} else {
  // 		return engagementList;
  // 	}
  // };

  const allEngagements = useMemo(() => {
    return (
      data?.pages?.reduce((acc, page) => {
        if (!page?.data) return acc;
        return [...acc, ...page.data];
      }, []) ?? []
    );
  }, [data]);

  const filteredData = useMemo(() => {
    if (
      name?.length > 0 ||
      manager?.length > 0 ||
      partner?.length > 0 ||
      clientname?.length > 0 ||
      country?.length > 0 ||
      engagementStatus?.length > 0 ||
      pfaStatus?.length > 0 ||
      competency?.length > 0 ||
      serviceLine?.length > 0
    ) {
      return allEngagements.filter((item) => {
        const nameMatch =
          name?.length > 0 ? name.some((n) => item?.title === n) : true;

        const managerMatch =
          manager?.length > 0 ? manager.includes(item?.manager) : true;

        const partnerMatch =
          partner?.length > 0 ? partner.includes(item?.partner) : true;

        const clientNameMatch =
          clientname?.length > 0
            ? clientname.includes(item?.clientname ?? item?.clientName ?? "")
            : true;

        const countryMatch =
          country?.length > 0 ? country.includes(item?.country ?? "") : true;

        const competencyMatch =
          competency?.length > 0
            ? competency.includes(item?.competency ?? "")
            : true;

        const serviceLineMatch =
          serviceLine?.length > 0
            ? serviceLine.includes(item?.serviceLine ?? "")
            : true;

        const statusMatch =
          engagementStatus?.length > 0
            ? engagementStatus
              .map((s) => s?.toLowerCase?.() ?? "")
              .includes(item?.engagementStatus?.toLowerCase?.() ?? "")
            : true;

        const pfaStatusMatch =
          pfaStatus?.length > 0
            ? pfaStatus.includes(item?.pfaStatus ?? "")
            : true;

        return (
          nameMatch &&
          managerMatch &&
          partnerMatch &&
          clientNameMatch &&
          countryMatch &&
          competencyMatch &&
          serviceLineMatch &&
          statusMatch &&
          pfaStatusMatch
        );
      });
    }
    return allEngagements;
  }, [
    name,
    manager,
    partner,
    clientname,
    country,
    engagementStatus,
    pfaStatus,
    competency,
    serviceLine,
    allEngagements,
  ]);

  const [localData, setLocalData] = useState(filteredData);

  const dropdownengagements = useMemo(() => {
    return data?.pages?.reduce((acc, page) => {
      if (!page?.data) return acc;
      return [...acc, ...page.data];
    }, []);
  }, [data]);

  useEffect(() => {
    if (!isEqual(localData, filteredData)) {
      setLocalData(filteredData);
    }
  }, [filteredData]);

  let engagementFilterDropdownData = engagementDropdownDataRes?.data;

  const areAnyFiltersApplied = () => {
    const engagementStatusIsUserApplied =
      engagementStatus?.length > 0 &&
      !(engagementStatus?.length === 1 && engagementStatus[0] === "Active");
    return (
      name?.length > 0 ||
      manager?.length > 0 ||
      partner?.length > 0 ||
      clientname?.length > 0 ||
      country?.length > 0 ||
      engagementStatusIsUserApplied ||
      pfaStatus?.length > 0 ||
      competency?.length > 0 ||
      serviceLine?.length > 0
    );
  };

  const noFiltersApplied = !areAnyFiltersApplied();

  const displayedList = useMemo(() => {
    const allowedStatuses = ["Active", "Not Started", "Not Approved"];
    const isFilterActive = !noFiltersApplied;
    if (isFilterActive) {
      if (dropdownFilteredList && dropdownFilteredList?.length > 0) {
        return dropdownFilteredList;
      }
      if (filteredData && filteredData?.length > 0) {
        return filteredData;
      }
      return [];
    }
    if (!engagements || engagements?.length === 0) return [];
    return engagements.filter(it =>
      allowedStatuses?.includes(String(it?.engagementStatus ?? "").trim())
    );
  }, [noFiltersApplied, dropdownFilteredList, filteredData, engagements]);

  useEffect(() => {
    let isMounted = true;
    const loadAll = async () => {
      if (!engagementStatus?.length) {
        while (isMounted && hasNextPage) {
          await fetchNextPage();
          await new Promise(r => setTimeout(r, 100));
        }
      }
    };
    loadAll();
    return () => { isMounted = false; };
  }, [engagementStatus, fetchNextPage, hasNextPage]);

  useEffect(() => {
    if (noFiltersApplied && engagements?.length === 0 && hasNextPage) {
      fetchNextPage();
    }
  }, [noFiltersApplied, engagements?.length, fetchNextPage, hasNextPage]);

  const handleLoadMore = async () => {
    if (noFiltersApplied) {
      if (hasNextPage) await fetchNextPage();
    } else {
      if (hasfilternextpage) await fetchnext();
    }
  };

  if (loadingRoute || isDeleting) {
    return (
      <StyledLoader>
        <div>
          <Spin size="large" />
        </div>
      </StyledLoader>
    );
  }

  return (
    <>
      <Modal
        title="Are you sure you want to delete this engagement?"
        visible={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => !deletingId && setDeleteModalVisible(false)} // Prevent closing while deleting
        okText={deletingId ? <Spin size="small" /> : "Yes"}
        cancelText="No"
        centered
        style={{ borderRadius: "30px" }}
        bodyStyle={{ fontSize: "16px", borderRadius: "30px" }}
      >
        <p>This action cannot be undone.</p>
      </Modal>
      <EngagementWrapper>
        <div className="title">
          <Title>Engagement </Title>
          <div>
            <Button
              type="primary"
              onClick={() => {
                history.push("/engagementCalendar");
              }}
            >
              Calendar View
            </Button>
            <Button
              type="primary"
              onClick={() => {
                history.push("/add-engagement", {
                  state: { personInCharge: false }, // or true / false
                });
              }}
            >
              Add New
            </Button>
          </div>
        </div>
        <Row>
          <Col sm={24}>
            <Form
              form={form}
              layout="vertical"
              className="mt-2"
              initialValues={{ engagementStatus: ["Active", "Not Started", "Not Approved"] }}
              onValuesChange={() => form.submit()}
            >
              <Row gutter={12} className="g-3 mb-2">
                {/* Title */}
                <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                  <DropdownCheckBox
                    name="title"
                    label="Title"
                    options={uniqBy(
                      engagementFilterDropdownData?.filter(i => i?.jobTitle)?.map(i => ({
                        label: String(i?.jobTitle).trim(),
                        value: String(i?.jobTitle).trim(),
                      })),
                      "value"
                    )}
                    form={form}
                  />
                </Col>

                {/* Client Name */}
                <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                  <DropdownCheckBox
                    name="clientname"
                    label="Client Name"
                    options={uniqBy(
                      name?.length > 0 ||
                        country?.length > 0 ||
                        manager?.length > 0 ||
                        engagementStatus?.length > 0 ||
                        pfaStatus?.length > 0 ||
                        partner?.length > 0 ||
                        competency?.length > 0 ||
                        serviceLine?.length > 0
                        ? displayedList?.map((item) => ({
                          label: String(item?.clientName ?? "Unknown").trim(),
                          value: String(item?.clientName ?? "Unknown"),
                        }))
                        : engagementFilterDropdownData?.map((item) => ({
                          label: String(item?.clientName ?? "Unknown").trim(),
                          value: String(item?.clientName ?? "Unknown"),
                        })),
                      "value"
                    )}
                    form={form}
                  />
                </Col>

                {/* Person In Charge */}
                <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                  <DropdownCheckBox
                    name="manager"
                    label="Person In Charge"
                    options={uniqBy(
                      name?.length > 0 ||
                        clientname?.length > 0 ||
                        country?.length > 0 ||
                        engagementStatus?.length > 0 ||
                        pfaStatus?.length > 0 ||
                        partner?.length > 0 ||
                        competency?.length > 0 ||
                        serviceLine?.length > 0
                        ? displayedList?.map((item) => {
                          const managerName =
                            find(userList, { userid: item?.manager })
                              ?.fullname ?? "Unknown";
                          return {
                            label: managerName,
                            value: String(item?.manager ?? managerName),
                          };
                        })
                        : engagementFilterDropdownData?.map((item) => {
                          const managerName =
                            find(userList, { userid: item?.personIncharge })
                              ?.fullname ?? "Unknown";
                          return {
                            label: managerName,
                            value: String(
                              item?.personIncharge ?? managerName
                            ),
                          };
                        }),
                      "value"
                    )}
                    form={form}
                  />
                </Col>

                {/* Partner */}
                <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                  <DropdownCheckBox
                    name="partner"
                    label="Partner"
                    options={uniqBy(
                      name?.length > 0 ||
                        manager?.length > 0 ||
                        clientname?.length > 0 ||
                        country?.length > 0 ||
                        engagementStatus?.length > 0 ||
                        pfaStatus?.length > 0 ||
                        competency?.length > 0 ||
                        serviceLine?.length > 0
                        ? displayedList?.map((item) => {
                          const partnerName =
                            find(userList, { userid: item?.partner })
                              ?.fullname ?? "Unknown";
                          return {
                            label: partnerName,
                            value: String(item?.partner ?? partnerName),
                          };
                        })
                        : engagementFilterDropdownData?.map((item) => {
                          const partnerName =
                            find(userList, { userid: item?.partner })
                              ?.fullname ?? "Unknown";
                          return {
                            label: partnerName,
                            value: String(item?.partner ?? partnerName),
                          };
                        }),
                      "value"
                    )}
                    form={form}
                  />
                </Col>

                {/* Country (restricted) */}
                {filteruser &&
                  importantPeople.includes(filteruser[0]?.userid) && (
                    <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                      <DropdownCheckBox
                        name="country"
                        label="Country"
                        options={uniqBy(
                          name?.length > 0 ||
                            manager?.length > 0 ||
                            clientname?.length > 0 ||
                            engagementStatus?.length > 0 ||
                            pfaStatus?.length > 0 ||
                            partner?.length > 0 ||
                            competency?.length > 0 ||
                            serviceLine?.length > 0
                            ? displayedList?.map((item) => ({
                              label: String(
                                item?.country ?? "Unknown"
                              ).trim(),
                              value: String(item?.country ?? "Unknown"),
                            }))
                            : engagementList?.map((item) => ({
                              label: String(
                                item?.country ?? "Unknown"
                              ).trim(),
                              value: String(item?.country ?? "Unknown"),
                            })),
                          "value"
                        )}
                        form={form}
                      />
                    </Col>
                  )}

                {/* Engagement Status */}
                <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                  <DropdownCheckBox
                    name="engagementStatus"
                    label="Engagement Status"
                    options={[
                      { label: "Active", value: "Active" },
                      { label: "Not Started", value: "Not Started" },
                      { label: "Not Approved", value: "Not Approved" },
                      { label: "Closed", value: "Closed" },
                    ]}
                    form={form}
                  />
                </Col>

                {/* PFA Status */}
                <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                  <DropdownCheckBox
                    name="pfaStatus"
                    label="PFA Status"
                    options={uniqBy(
                      name?.length > 0 ||
                        manager?.length > 0 ||
                        clientname?.length > 0 ||
                        country?.length > 0 ||
                        engagementStatus?.length > 0 ||
                        partner?.length > 0 ||
                        competency?.length > 0 ||
                        serviceLine?.length > 0
                        ? displayedList?.map((item) => ({
                          label: String(item?.pfaStatus ?? "Unknown").trim(),
                          value: String(item?.pfaStatus ?? "Unknown"),
                        }))
                        : dropdownengagements?.map((item) => ({
                          label: String(item?.pfaStatus ?? "Unknown").trim(),
                          value: String(item?.pfaStatus ?? "Unknown"),
                        })),
                      "value"
                    )}
                    form={form}
                  />
                </Col>

                {/* Competency (partner only) */}
                {isPartner && (
                  <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                    <DropdownCheckBox
                      name="competency"
                      label="Competency"
                      options={uniqBy(
                        name?.length > 0 ||
                          manager?.length > 0 ||
                          clientname?.length > 0 ||
                          country?.length > 0 ||
                          pfaStatus?.length > 0 ||
                          engagementStatus?.length > 0 ||
                          partner?.length > 0 ||
                          serviceLine?.length > 0
                          ? displayedList?.map((item) => ({
                            label: String(
                              item?.competency ?? "Unknown"
                            ).trim(),
                            value: String(item?.competency ?? "Unknown"),
                          }))
                          : engagementFilterDropdownData?.map((item) => ({
                            label: String(
                              item?.competency ?? "Unknown"
                            ).trim(),
                            value: String(item?.competency ?? "Unknown"),
                          })),
                        "value"
                      )}
                      form={form}
                    />
                  </Col>
                )}

                {/* Service Line (partner only) */}
                {isPartner && (
                  <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                    <DropdownCheckBox
                      name="serviceLine"
                      label="Service Line"
                      options={uniqBy(
                        name?.length > 0 ||
                          manager?.length > 0 ||
                          clientname?.length > 0 ||
                          country?.length > 0 ||
                          pfaStatus?.length > 0 ||
                          engagementStatus?.length > 0 ||
                          partner?.length > 0 ||
                          competency?.length > 0
                          ? displayedList?.map((item) => ({
                            label: String(
                              item?.serviceLine ?? "Unknown"
                            ).trim(),
                            value: String(item?.serviceLine ?? "Unknown"),
                          }))
                          : engagementFilterDropdownData?.map((item) => ({
                            label: String(
                              item?.serviceLine ?? "Unknown"
                            ).trim(),
                            value: String(item?.serviceLine ?? "Unknown"),
                          })),
                        "value"
                      )}
                      form={form}
                    />
                  </Col>
                )}
              </Row>
            </Form>
          </Col>
        </Row>

        <div className="people_font_style mt-2">
          Engagement Count: {displayedList?.length}
        </div>
        <Spin spinning={isLoading || isEngagementFilterLoading}>
          <TableContainer
            isHidden={!displayedList || displayedList?.length === 0}
          >
            <StyledTable
              dataSource={displayedList}
              columns={columns}
              pagination={false}
              scroll={{ y: 400 }}
              components={vt}
              rowClassName={(record, index) =>
                index % 2 === 0 ? "" : "non-white"
              }
              rowKey="engagementId"
              onRow={(record, index) => {
                // keep your existing last-row trigger if you like,
                // or rely on the Load more button below
                if (
                  index === displayedList?.length - 1 &&
                  noFiltersApplied &&
                  hasNextPage &&
                  !isReloading
                ) {
                  // optional auto-load on last row render
                  // fetchNextPage();
                } else if (
                  index === displayedList?.length - 1 &&
                  !noFiltersApplied &&
                  hasfilternextpage &&
                  !isfilterreloading
                ) {
                  // fetchnext();
                }
              }}
              footer={() => {
                const showLoading =
                  (isReloading && noFiltersApplied) || (isReloading && !noFiltersApplied) ||
                  (isfilterreloading && !noFiltersApplied);
                const hasMore =
                  (noFiltersApplied && hasNextPage) ||
                  (!noFiltersApplied && hasfilternextpage);

                return (
                  <div style={{ textAlign: "center", padding: 8 }}>
                    {showLoading ? (
                      <Spin size="large" tip="Loading more..." />
                    ) : hasMore ? (
                      <button
                        type="button"
                        onClick={() => {
                          handleLoadMore()
                        }}
                        style={{
                          border: "1px solid #e8e8e8",
                          padding: "6px 12px",
                          borderRadius: 4,
                          background: "white",
                          cursor: "pointer",
                        }}
                      >
                        Click to Load more...
                      </button>
                    ) : (
                      // ✅ All data loaded message
                      <span style={{ color: "#888" }}>All data loaded</span>
                    )}
                  </div>
                );
              }}
            />
          </TableContainer>
        </Spin>

        {!isLoading ||
          (!isEngagementFilterLoading && (
            <div className="pagination-loader">
              <Spin spinning={isReloading} />
            </div>
          ))}
      </EngagementWrapper>
    </>
  );
};

export default Engagement;
